/**
 * Scene boundaries for commands that create scenes mid-edit — transcript
 * restore and trim-extend hand these to `CreateScenesForRestoredRegionCommand`.
 */

import { buildSpeakerScenes } from '@src/services/framing/zoomCall/buildSpeakerTurns';

import type { SceneDetection } from '@src/editor/types/sceneDetection';
import type { TranscriptWord } from '@src/services/framing/speakerIntervalUtils';

const WIDESCREEN_ASPECT = 16 / 9;
const ASPECT_TOLERANCE = 0.01;

export interface ResolveSceneDetectionsForEditInput {
  /** Shot detections (camera cuts) in media time. */
  shots: ReadonlyArray<SceneDetection> | null | undefined;
  /** The original transcript, in media time. */
  transcript: ReadonlyArray<TranscriptWord> | null | undefined;
  /** Timeline width / height. */
  aspectRatio: number;
  /** The story's speaker→face map; null when framing is face-only. */
  speakerMap: Record<string, string[]> | null;
}

/**
 * When the edit's scenes are framed by speaker (a cropped aspect ratio and a
 * speaker→face map), boundaries are speaker turns split at camera cuts — the
 * same scenes the aspect-ratio switch creates. The restore command extends a
 * neighbouring scene that came from the same range, so restoring the same
 * speaker's words keeps their scene and only a different speaker gets a new
 * one. Otherwise (16:9, or no mapping) it is the camera cuts alone.
 */
export function resolveSceneDetectionsForEdit({
  shots,
  transcript,
  aspectRatio,
  speakerMap,
}: ResolveSceneDetectionsForEditInput): SceneDetection[] | undefined {
  const shotDetections = shots && shots.length > 0 ? [...shots] : undefined;

  const framesBySpeaker =
    !!speakerMap &&
    Object.keys(speakerMap).length > 0 &&
    Math.abs(aspectRatio - WIDESCREEN_ASPECT) >= ASPECT_TOLERANCE;
  if (!framesBySpeaker || !transcript || transcript.length === 0) {
    return shotDetections;
  }

  const speakerScenes = buildSpeakerScenes(transcript, shots ?? []);
  return speakerScenes.length > 0 ? speakerScenes : shotDetections;
}
